import { Button, IconButton, Text } from "@aurem/ui";
import { ChevronLeftIcon, XIcon } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";
import { Dispatch, SetStateAction } from "react";

import { TourType, UserTourStep } from "types";
import { useUpdateUserTour } from "../hooks/update-dashboard-tour.hooks";

interface TourStepContentProps {
  steps: number;
  currentStep: number;
  heading: string;
  description: string;
  setCurrentStep: Dispatch<SetStateAction<number>>;
  tourType: TourType;
}

export const TourStepContent: FC<TourStepContentProps> = ({
  steps,
  currentStep,
  heading,
  description,
  setCurrentStep,
  tourType,
}) => {
  const { t } = useI18n();

  const { updateTour } = useUpdateUserTour();

  const finishTour = async () => {
    if (currentStep < steps) {
      setCurrentStep(steps);
    }

    await updateTour({
      step: UserTourStep.Finished,
      type: tourType,
    });
  };

  const handleNext = async () => {
    setCurrentStep(currentStep + 1);
    if (currentStep + 1 === steps) {
      await finishTour();
    }
  };

  const handleBack = async () => {
    setCurrentStep(currentStep - 1);
  };

  return (
    <div className="flex flex-col">
      <div className="flex flex-col flex-1">
        <div className="flex flex-col">
          <div className="flex justify-between items-center p-4">
            <Text weight="medium">{heading}</Text>
            <IconButton variant="ghost" onClick={finishTour}>
              <XIcon />
            </IconButton>
          </div>
          <div className="h-1 border-t border-neutral-500/10" />
          <Text className="p-4">{description}</Text>
        </div>

        <div className="px-4 pb-4 flex justify-between items-center">
          {currentStep > 0 ? (
            <Button
              variant="text"
              onClick={handleBack}
              className="no-underline"
              startIcon={
                <ChevronLeftIcon size={16} className="rtl:-scale-x-100" />
              }
            >
              {t(
                "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.navigation.back",
              )}
            </Button>
          ) : (
            <div />
          )}

          <div className="flex justify-end items-center mt-auto gap-3">
            {steps > 1 && (
              <Text size="sm" className="text-neutral-500/50">
                {t(
                  "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.navigation.stepCounter",
                  {
                    currentStep: currentStep + 1,
                    steps: steps,
                  },
                )}
              </Text>
            )}

            <Button onClick={handleNext}>
              {steps === 1
                ? t(
                    "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.navigation.gotIt",
                  )
                : currentStep < steps - 1
                  ? t(
                      "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.navigation.next",
                    )
                  : t(
                      "apps.admin.pages.companies.companyId.dashboard.overview.page.tour.navigation.finishTour",
                    )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
